/**
 * RiskVulnerabilities — Risk module Vulnerabilities tab.
 *
 * Replaces the previous static mock list. Reads from
 * /api/v1/rsk/vulnerabilities, which captures every vulnerability raised
 * against a risk during the assessment phase (analogue of control
 * deficiencies). Look-and-feel kept consistent with the controls
 * "Deficiencies" surface — severity + status pills, source label, raised-by.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, AlertTriangle } from 'lucide-react';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
// merged: incoming + existing
import {
    riskVulnerabilityService,
    RiskVulnerability,
    VULNERABILITY_SOURCES,
    VULNERABILITY_SEVERITIES,
    VULNERABILITY_STATUSES,
    VulnerabilitySeverity,
    VulnerabilityStatus,
    VulnerabilitySource,
} from '../../services/riskVulnerabilityService';
import extractApiError from '../../utils/apiError';
import Skeleton from '../common/Skeleton';
import { usePermission } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const SEVERITY_CLASS: Record<string, string> = {
    Critical:      'bg-red-100 text-red-700',
    High:          'bg-orange-100 text-orange-700',
    Medium:        'bg-yellow-100 text-yellow-700',
    Low:           'bg-blue-100 text-blue-700',
    Informational: 'bg-gray-100 text-gray-600',
};

const STATUS_CLASS: Record<string, string> = {
    Open:           'bg-rose-100 text-rose-700',
    'In-Progress':  'bg-amber-100 text-amber-700',
    Resolved:       'bg-emerald-100 text-emerald-700',
    Closed:         'bg-gray-100 text-gray-600',
};

const RiskVulnerabilities: React.FC = () => {
    const { formatDate } = useEntitySettings();
    const { allowed: canView, loading: permLoading } = usePermission('RSK_VULNERABILITIES', 'view');
    const [rows, setRows] = useState<RiskVulnerability[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [sourceFilter, setSourceFilter] = useState<'' | VulnerabilitySource>('');
    const [severityFilter, setSeverityFilter] = useState<'' | VulnerabilitySeverity>('');
    const [statusFilter, setStatusFilter] = useState<'' | VulnerabilityStatus>('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await riskVulnerabilityService.listAll({
                source:   sourceFilter   || undefined,
                severity: severityFilter || undefined,
                status:   statusFilter   || undefined,
                search:   search.trim()  || undefined,
                limit: 200,
            });
            setRows(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load vulnerabilities') });
        } finally {
            setLoading(false);
        }
    }, [search, sourceFilter, severityFilter, statusFilter]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { setPage(1); }, [search, sourceFilter, severityFilter, statusFilter, pageSize]);

    const paged = useMemo(
        () => rows.slice((page - 1) * pageSize, page * pageSize),
        [rows, page, pageSize],
    );

    // ── Not-authorised gate ─────────────────────────────────────────────────
    if (!permLoading && !canView) {
        return (
            <NoDataFound title="Not authorised" message="You do not have access to 'RISK_VULNERABILITIES'." />
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.SearchSelect
                        value={sourceFilter}
                        onChange={v => setSourceFilter((v || '') as any)}
                        className="filter-select"
                        data-testid="SEL_risk_vuln_source_filter"
                        noSearchIcon
                        options={[
                            { value: '', label: 'All Sources' },
                            ...VULNERABILITY_SOURCES.map(s => ({
                                value: s,
                                label: s.replace('_', '-').replace(/\b\w/g, c => c.toUpperCase()),
                            })),
                        ]}
                    />
                    <FormControl.SearchSelect
                        value={severityFilter}
                        onChange={v => setSeverityFilter((v || '') as any)}
                        className="filter-select"
                        data-testid="SEL_risk_vuln_severity_filter"
                        noSearchIcon
                        options={[
                            { value: '', label: 'All Severities' },
                            ...VULNERABILITY_SEVERITIES.map(s => ({ value: s, label: s })),
                        ]}
                    />
                    <FormControl.SearchSelect
                        value={statusFilter}
                        onChange={v => setStatusFilter((v || '') as any)}
                        className="filter-select"
                        data-testid="SEL_risk_vuln_status_filter"
                        noSearchIcon
                        options={[
                            { value: '', label: 'All Statuses' },
                            ...VULNERABILITY_STATUSES.map(s => ({ value: s, label: s })),
                        ]}
                    />
                    <FormControl.Input
                        type="text"
                        placeholder="Search vulnerabilities…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_risk_vuln_search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        {!loading && rows.length === 0 ? (
                            <NoDataFound
                                title="No Vulnerabilities Yet"
                                message='Vulnerabilities raised during risk assessment appear here. Open a risk in the register and click "Raise Vulnerability".'
                            />
                        ) : (
                        <>
                            <div className="table-list-scroll">
                                <table className="table-list-table">
                                    <thead>
                                        <tr>
                                            <th className="table-list-th table-list-cell--sno">S.No</th>
                                            <th className="table-list-th">Risk</th>
                                            <th className="table-list-th">Source</th>
                                            <th className="table-list-th">Detected</th>
                                            <th className="table-list-th">Severity</th>
                                            <th className="table-list-th">Status</th>
                                            <th className="table-list-th">Description</th>
                                            <th className="table-list-th">Raised By</th>
                                        </tr>
                                    </thead>
                                    <tbody aria-busy={loading} aria-live="polite">
                                        {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', '2xs', 'pill', 'pill', 'md', 'sm']} rows={5} />}
                                        {!loading && paged.map((v, idx) => (
                                            <tr key={v.id} className="table-list-row">
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title">
                                                        {v.risk_code || `Risk #${v.risk_id}`}
                                                    </span>
                                                    {v.risk_name && <span className="table-list-cell-sub">{v.risk_name}</span>}
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="text-xs capitalize">{v.source.replace('_', '-')}</span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="text-xs text-gray-600">{formatDate(v.detected_on)}</span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_CLASS[v.detected_severity] || 'bg-gray-100 text-gray-500'}`}>
                                                        {v.detected_severity}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASS[v.status] || 'bg-gray-100 text-gray-500'}`}>
                                                        {v.status}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell table-list-cell--truncate" title={v.description}>
                                                    <span className="inline-flex items-center gap-1">
                                                        <AlertTriangle size={12} className="text-rose-500 shrink-0" />
                                                        {v.description}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">{v.created_by_name || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalRecords={rows.length}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                recordLabel="vulnerabilities"
                            />
                        </>
                        )}
                    </div>
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default RiskVulnerabilities;
